// TanStack Router's head() function accepts meta as React.JSX.IntrinsicElements['meta'][].
// The router also handles a special { title: string } sentinel that becomes <title>.
type RouteMeta = React.JSX.IntrinsicElements["meta"] | { title: string };
type RouteLink = React.JSX.IntrinsicElements["link"];

export interface SeoOptions {
  /** Page-specific title. Gets the " | Forged" suffix appended. Use
   * `absoluteTitle` instead for a title that must appear exactly as given
   * (the homepage's, which already contains "Forged" itself). */
  title?: string;
  /** A complete title, used exactly as supplied — no " | Forged" suffix. */
  absoluteTitle?: string;
  description?: string;
  /** Site-relative path (e.g. "/about") this page canonicalizes to. Also
   * becomes og:url. Omit for non-indexable routes (auth pages, the
   * authenticated app) — no canonical/og:url is emitted without it, rather
   * than defaulting every unspecified route to "/". */
  canonicalPath?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  twitterCard?: "summary" | "summary_large_image";
  /** Thin/placeholder content that shouldn't be indexed yet (e.g. /api) —
   * emits `robots: noindex, nofollow` and skips canonical/OG/Twitter
   * entirely, rather than publishing social metadata for a page with
   * nothing real to share. */
  noindex?: boolean;
}

const SITE_NAME = "Forged";
/** Exported for sitemap.ts, which resolves the same canonical URLs this file
 *  does (buildHead's canonicalUrl) but outside of any per-route SeoOptions
 *  call. */
export const SITE_URL = "https://tryforged.ai";
const DEFAULT_TITLE = "Forged - Turn your idea into software that keeps improving";
const DEFAULT_DESCRIPTION =
  "Describe what you want to build. Forged turns your idea into a structured product plan, converts that plan into implementation-ready work, and coordinates AI agents to build it, take it live, and keep improving it.";
const DEFAULT_OG_IMAGE = `${SITE_URL}/social/forged-og.png`;
const DEFAULT_OG_IMAGE_ALT = "Forged, the AI product-building system for founders";

/** A page's tab title, "Task | Forged", as buildHead writes it. */
export function pageTitle(title: string): string {
  return `${title} | ${SITE_NAME}`;
}

export function buildHead(opts: SeoOptions = {}): { meta: RouteMeta[]; links: RouteLink[] } {
  const title = opts.absoluteTitle ?? (opts.title ? pageTitle(opts.title) : DEFAULT_TITLE);
  const description = opts.description ?? DEFAULT_DESCRIPTION;

  if (opts.noindex) {
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "noindex, nofollow" },
      ],
      links: [],
    };
  }

  const ogTitle = opts.ogTitle ?? title;
  const ogDescription = opts.ogDescription ?? description;
  const ogImage = opts.ogImage ?? DEFAULT_OG_IMAGE;
  const ogImageAlt = opts.ogImageAlt ?? DEFAULT_OG_IMAGE_ALT;
  const canonicalUrl = opts.canonicalPath ? new URL(opts.canonicalPath, SITE_URL).toString() : undefined;

  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:title", content: ogTitle },
      { property: "og:description", content: ogDescription },
      ...(canonicalUrl ? [{ property: "og:url", content: canonicalUrl }] : []),
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: ogImageAlt },
      { name: "twitter:card", content: opts.twitterCard ?? "summary_large_image" },
      { name: "twitter:title", content: ogTitle },
      { name: "twitter:description", content: ogDescription },
      { name: "twitter:image", content: ogImage },
    ],
    links: canonicalUrl ? [{ rel: "canonical", href: canonicalUrl }] : [],
  };
}
